import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useMemo, type JSX } from "react";
import { z } from "zod";

import { cn } from "@repo/cn";
import Confetti from "@repo/icons/colored/Confetti";
import ExclamationCircle from "@repo/icons/default/ExclamationCircle";
import { CpsAvatar } from "corpus";

import { RecurrenceDayItem } from "@/components/recurring-appointment/recurrence-day-item";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { Button } from "@/components/ui/button/button";
import { Page } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import { useGraphQL } from "@/hooks/use-graphql";
import { formatPhoneNumber } from "@/lib/helpers/misc";
import {
  buildRecurrenceSlots,
  deriveUnitSwap,
  formatRecurrenceScheduleLabel,
  formatSessionDateLabel,
  getRecurrenceBaseUnit,
  getSessionDateBox,
} from "@/lib/helpers/recurrence";
import {
  mapNodeToRaw,
  RecurringAppointmentDetailPageQuery,
} from "@/lib/queries/recurrence-detail-query";
import { getNameInitials } from "@/lib/string";

const parseDates = (value?: string): string[] =>
  value ? value.split(",").filter(Boolean) : [];

const RecurringAppointmentExtendSuccessPage = (): JSX.Element => {
  const { recurringAppointmentId, criadas } = Route.useSearch();
  const router = useRouter();

  const queryResult = useGraphQL(RecurringAppointmentDetailPageQuery, {
    codAgendamentoRecorrente: recurringAppointmentId,
  });
  const { data } = queryResult;

  const recurrence = data?.agendamentosRecorrentes?.[0] ?? null;
  const patientPhone =
    data?.agendamentos?.find((appointment) => appointment.Paciente?.telefone)?.Paciente
      ?.telefone ?? null;

  const scheduled = useMemo(() => parseDates(criadas), [criadas]);

  const created = useMemo(() => {
    const roots = (data?.agendamentos ?? []).map(mapNodeToRaw);
    const slots = buildRecurrenceSlots(roots, recurringAppointmentId);
    const baseUnit = getRecurrenceBaseUnit(slots, recurrence?.codUnidade);

    return scheduled.map((sessionDate) => {
      const appointment = slots.find(
        (slot) => slot.data.slice(0, 10) === sessionDate.slice(0, 10),
      );

      return {
        data: sessionDate,
        swap: appointment ? deriveUnitSwap(baseUnit, appointment) : null,
      };
    });
  }, [data?.agendamentos, recurringAppointmentId, recurrence?.codUnidade, scheduled]);

  const changed = created.filter((date) => date.swap !== null);

  const title =
    scheduled.length === 1
      ? "1 agendamento adicionado"
      : `${scheduled.length} agendamentos adicionados`;

  const goToRecurrence = (): void => {
    router.history.go(-2);
  };

  return (
    <RoundedLayout>
      <Page>
        <div className="flex min-h-full flex-col">
          <div className="flex flex-col gap-4">
            <Confetti size={64} />
            <h1 className="font-semibold text-neutral-700 text-xl">{title}</h1>

            <AsyncDataWrapper {...queryResult}>
              {recurrence ? (
                <div className="flex flex-col gap-4 rounded-lg border-[1.5px] border-neutral-100 p-4">
                  <div className="flex flex-row items-center gap-3">
                    <CpsAvatar
                      alt={recurrence.nomePaciente ?? ""}
                      color="primaryLight"
                      initials={getNameInitials(recurrence.nomePaciente ?? "") ?? ""}
                      size={40}
                    />
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-neutral-700">
                        {recurrence.nomePaciente}
                      </span>
                      {patientPhone ? (
                        <span className="text-neutral-500 text-xs">
                          {formatPhoneNumber(patientPhone)}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className="border-neutral-100 border-t pt-3">
                    <span className="text-neutral-600 text-sm">
                      {formatRecurrenceScheduleLabel(recurrence)}
                    </span>
                  </div>

                  {created.length > 0 ? (
                    <div className="grid grid-cols-3 gap-2">
                      {created.map((date) => {
                        const box = getSessionDateBox(date.data);
                        return (
                          <div
                            key={date.data}
                            className={cn(
                              "flex flex-row items-center justify-center gap-1 rounded-sm px-2 py-2 text-xs",
                              date.swap ? "bg-warning-50" : "bg-secondary-50",
                            )}
                          >
                            <span className="font-medium text-neutral-700">
                              {box.day}
                            </span>
                            <span className="text-neutral-500">{box.month}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}

                  {changed.length > 0 ? (
                    <div className="flex flex-col gap-6 border-neutral-100 border-t pt-3">
                      {changed.map((date) => (
                        <div
                          key={date.data}
                          className="flex flex-row flex-wrap items-center gap-x-2 gap-y-1"
                        >
                          <ExclamationCircle
                            size={16}
                            className="shrink-0 fill-warning-500"
                          />
                          <span className="shrink-0 font-medium text-neutral-700 text-xs">
                            {formatSessionDateLabel(date.data)}
                          </span>
                          <div className="w-full rounded-sm bg-neutral-50 px-3 py-1">
                            {date.swap ? (
                              <RecurrenceDayItem.UnitSwap
                                from={date.swap.from}
                                to={date.swap.to}
                                semReservaDeSala={date.swap.semReservaDeSala}
                              />
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </AsyncDataWrapper>
          </div>

          <div className="mt-auto flex flex-col gap-2 pt-6">
            <Button fullWidth onClick={goToRecurrence}>
              Ver recorrência
            </Button>
          </div>
        </div>
      </Page>
    </RoundedLayout>
  );
};

export const Route = createFileRoute("/recurring-appointments/extend/success")({
  component: RecurringAppointmentExtendSuccessPage,
  validateSearch: z.object({
    recurringAppointmentId: z.number(),
    criadas: z.string().optional(),
  }),
});
